import type { Encoding, KeyType } from "@crossmint/client-signers";
import type { Ed25519, Secp256k1 } from "open-signer-shared-cryptography";
import { secureWipe } from "../common/secure-memory";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type {
	CryptoStrategy,
	PublicKey,
	Signature,
} from "./crypto-key-strategy";
import { Ed25519Strategy } from "./strategies/ed25519";
import { Secp256k1Strategy } from "./strategies/secp256k1";

export class CryptoKeyService extends CrossmintFrameService {
	name = "Crypto Key Service";
	log_prefix = "[CryptoKeyService]";

	private strategies: Map<KeyType, CryptoStrategy<KeyType>>;

	constructor(
		protected readonly logger: FrameLogger,
		protected readonly ed25519: Ed25519,
		protected readonly secp256k1: Secp256k1,
	) {
		super(logger);
		this.strategies = new Map();

		this.registerStrategy(new Ed25519Strategy(ed25519));
		this.registerStrategy(new Secp256k1Strategy(secp256k1));
	}

	private registerStrategy<K extends KeyType>(
		strategy: CryptoStrategy<K>,
	): void {
		this.strategies.set(strategy.keyType, strategy);
	}

	private getStrategy<K extends KeyType>(keyType: K): CryptoStrategy<K> {
		const strategy = this.strategies.get(keyType);
		if (!strategy) {
			throw new Error(`Unsupported key type: ${keyType}`);
		}
		return strategy as CryptoStrategy<K>;
	}

	async getPrivateKeyFromSeed<K extends KeyType>(
		keyType: K,
		seed: Uint8Array,
	): Promise<Uint8Array> {
		const strategy = this.getStrategy(keyType);
		return strategy.getPrivateKeyFromSeed(seed);
	}

	async getPublicKeyFromSeed<K extends KeyType>(
		keyType: K,
		seed: Uint8Array,
	): Promise<PublicKey<K>> {
		this.logger.debug(
			this.log_prefix,
			`Getting public key for type ${keyType}`,
		);
		const strategy = this.getStrategy(keyType);
		const privateKey = await strategy.getPrivateKeyFromSeed(seed);
		try {
			const publicKey = await strategy.getPublicKey(privateKey);
			return {
				...strategy.formatPublicKey(publicKey),
				keyType,
			};
		} finally {
			secureWipe(privateKey);
		}
	}

	async getAllPublicKeysFromSeed(
		seed: Uint8Array,
	): Promise<Record<KeyType, PublicKey<KeyType>>> {
		this.logger.debug(this.log_prefix, `Getting public keys`);
		const publicKeys: Partial<Record<KeyType, PublicKey<KeyType>>> = {};
		try {
			for (const keyType of this.strategies.keys()) {
				publicKeys[keyType] = await this.getPublicKeyFromSeed(keyType, seed);
			}
			return publicKeys as Record<KeyType, PublicKey<KeyType>>;
		} finally {
			secureWipe();
		}
	}

	async sign<K extends KeyType>(
		keyType: K,
		privateKey: Uint8Array,
		message: Uint8Array,
		options?: {
			responseEncoding?: Encoding;
		},
	): Promise<{ signature: Signature<K>; publicKey: PublicKey<K> }> {
		this.logger.debug(this.log_prefix, `Signing message for type ${keyType}`);
		const strategy = this.getStrategy(keyType);
		const signature = await strategy.sign(privateKey, message);
		this.logger.debug(this.log_prefix, `Message signed`);
		const publicKey = await strategy.getPublicKey(privateKey);
		return {
			signature: strategy.formatSignature(signature, options?.responseEncoding),
			publicKey: {
				...strategy.formatPublicKey(publicKey, options?.responseEncoding),
				keyType,
			},
		};
	}
}
